import { lightSimulation } from '../lib/lightSimulation';
import './ProductDetail.css';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { AddToProject } from '../components/AddToProject';
import { useAuth } from '../lib/auth';
import { supabase, type ProductPublic } from '../lib/supabase';
import { theme } from '../styles/theme';
import { Seo, productJsonLd, breadcrumbJsonLd } from '../lib/seo';
import { getTrackingSessionId, trackProductView, trackEvent } from '../lib/tracking';
import { useCurrency } from '../lib/currency';
import { useCustomer, discountForBrand } from '../lib/customer';
import { translateLutronDesc, prettifyLutronName } from '../lib/lutronTranslate';
import { thumbUrl } from '../lib/imageUrl';
import { variantPrice } from '../lib/variantPrice';
import { lutronSpecUrl, lutronSearchUrl } from '../lib/lutronSpec';
import { isPublicSpecKey } from '../lib/specLabels';
import { productModel } from '../lib/productModel';

type Variant = {
  id: string;
  product_id: string;
  variant_sku?: string | null;
  full_sku?: string | null;
  configuration_key?: string | null;
  sku_suffix?: string | null;
  color_temperature_k?: number | null;
  beam_angle_deg?: number | null;
  finish?: string | null;
  wattage_w?: number | null;
  voltage_v?: number | null;
  cri?: number | null;
  ip_rating?: string | null;
  dim_method?: string | null;
  msrp_usd?: number | null;
  msrp_mxn?: number | null;
  active: boolean;
  position?: number | null;
  attributes?: Record<string, unknown>;
  primary_image_url?: string | null;
};

type InventoryRow = {
  variant_id: string | null;
  product_id: string;
  location: string;
  on_hand: number;
  reserved: number;
};

type Availability = {
  variant_id: string | null;
  stock_policy: string;
  lead_time_days: number;
  supplier_country: string | null;
  availability_status: 'in_stock' | 'made_to_order' | 'restocking' | 'discontinued';
  available: number;
};

export function ProductDetail() {
  const { sku } = useParams<{ sku: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestedVariantSku = searchParams.get('v');  // ?v=SCR-15-MN → preseleccionar
  const { session } = useAuth();
  const [p, setP] = useState<ProductPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState<number | null>(null);
  const swipeX = useRef<number | null>(null);
  const [zoom, setZoom] = useState<{ x: number; y: number } | null>(null);
  const [related, setRelated] = useState<ProductPublic[]>([]);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [inventory, setInventory] = useState<InventoryRow[]>([]);
  const [availability, setAvailability] = useState<Availability[]>([]);
  const [showInventoryAll, setShowInventoryAll] = useState(false);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [sku]);

  useEffect(() => {
    if (!sku) return;
    setLoading(true);
    setActiveImg(null);
    setVariants([]);
    setSelectedVariantId(null);
    setInventory([]);
    setShowInventoryAll(false);
    supabase
      .from('products_public')
      .select('*')
      .eq('sku', sku)
      .maybeSingle()
      .then(async ({ data, error }) => {
        if (error) console.error(error);
        let prod = (data as ProductPublic | null) ?? null;

        // Si no encontramos producto canonical, ver si el SKU es una variante
        if (!prod) {
          const { data: vData } = await supabase
            .from('product_variants')
            .select('product_id, full_sku, configuration_key, product:products_public!inner(*)')
            .or(`full_sku.eq.${sku!},configuration_key.eq.${sku!}`)
            .limit(1)
            .maybeSingle();
          if (vData && (vData as any).product) {
            const variantSku = (vData as any).configuration_key ?? (vData as any).full_sku;
            const canonical = (vData as any).product as ProductPublic;
            // Redirigir a /producto/<canonical>?v=<variant>
            navigate(`/producto/${encodeURIComponent(canonical.sku)}?v=${encodeURIComponent(variantSku)}`, { replace: true });
            return;
          }
        }

        setP(prod);
        setLoading(false);
        if (prod) {
          trackProductView(prod.sku, prod.name); // ViewContent (retargeting) + marketing_events
          loadRelated(prod);
          loadVariants(prod.id);
          loadInventory(prod.id);
        }
      });
  }, [sku, requestedVariantSku]);

  const loadVariants = async (productId: string) => {
    const { data } = await supabase
      .from('product_variants')
      .select('id, product_id, full_sku, configuration_key, sku_suffix, attributes, finish, color_temperature_k, beam_angle_deg, wattage_w, dim_method, voltage_v, cri, ip_rating, stock_policy, supplier_lead_time_days, msrp_usd, msrp_mxn, primary_image_url, position, active')
      .eq('product_id', productId)
      .eq('active', true)
      .order('position', { ascending: true });
    const list = (data as Variant[]) ?? [];
    setVariants(list);
    if (list.length > 0) {
      // Si el URL tiene ?v=<sku>, preseleccionar esa variante
      if (requestedVariantSku) {
        const match = list.find(v => v.configuration_key === requestedVariantSku)
          ?? list.find(v =>
            v.full_sku === requestedVariantSku
            && v.attributes?.optica_instalada_origen === true
          )
          ?? list.find(v =>
            v.full_sku === requestedVariantSku
            || v.variant_sku === requestedVariantSku
          );
        if (match) {
          setSelectedVariantId(match.id);
          return;
        }
      }
      setSelectedVariantId(list[0].id);
    }
  };

  const loadInventory = async (productId: string) => {
    const [invRes, availRes] = await Promise.all([
      supabase
        .from('inventory')
        .select('variant_id, product_id, location, on_hand, reserved')
        .eq('product_id', productId),
      supabase
        .from('product_availability')
        .select('variant_id, stock_policy, lead_time_days, supplier_country, availability_status, available')
        .eq('product_id', productId),
    ]);
    setInventory((invRes.data as InventoryRow[]) ?? []);
    setAvailability((availRes.data as Availability[]) ?? []);
  };

  const availabilityFor = (variantId: string | null): Availability | null => {
    return availability.find(a => a.variant_id === variantId) ?? null;
  };

  // Stock disponible para la variante seleccionada (sumando todas las ubicaciones)
  const variantStock = (variantId: string | null): number => {
    const calculated = availabilityFor(variantId);
    if (calculated) return Math.max(0, Number(calculated.available ?? 0));
    if (!inventory.length) return 0;
    const rows = variantId
      ? inventory.filter(r => r.variant_id === variantId)
      : inventory.filter(r => r.variant_id == null);
    return rows.reduce((sum, r) => sum + Math.max(0, (r.on_hand ?? 0) - (r.reserved ?? 0)), 0);
  };

  const loadRelated = async (prod: ProductPublic) => {
    // Cross-sell: misma categoría (mismo bc_cat tag) o mismo voltaje
    const tagFilters = (prod.tags ?? []).filter(t => t.startsWith('bc_cat:')).slice(0, 2);
    if (!tagFilters.length) return;
    const { data } = await supabase
      .from('products_public')
      .select('id, sku, brand_code, name, msrp_mxn, msrp_usd, images, lutron_compatible')
      .neq('sku', prod.sku)
      .overlaps('tags', tagFilters)
      .limit(4);
    setRelated((data as ProductPublic[]) ?? []);
  };

  if (loading) return <Shell><LoadingSkeleton /></Shell>;
  if (!p) return <Shell>
    <div style={{ padding: 40, color: theme.brand, textAlign: 'center', fontFamily: theme.font.sans }}>
      Producto no encontrado.{' '}
      <Link to="/catalogo" style={{ color: theme.accentDark }}>Volver al catálogo</Link>.
    </div>
  </Shell>;

  const imgs = p.images ?? [];

  // Variante seleccionada (si existe) sustituye los precios y SKU
  const selectedVariant = variants.find(v => v.id === selectedVariantId) ?? null;
  const effectiveSku = selectedVariant?.full_sku ?? selectedVariant?.variant_sku ?? p.sku;
  const { usd: effectiveUsd, mxn: effectiveMxn } = variantPrice(p, selectedVariant, variants);
  const pricePending = effectiveUsd == null && effectiveMxn == null;

  const baseMainUrl = imgs.find(i => i.is_thumbnail)?.url ?? imgs[0]?.url;
  const main = activeImg != null ? imgs[activeImg] : { url: selectedVariant?.primary_image_url || baseMainUrl };
  const visibleIndex = activeImg ?? Math.max(0, imgs.findIndex(i => i.url === main?.url));
  const selectVariant = (id: string) => {
    setSelectedVariantId(id);
    setActiveImg(null);
    setZoom(null);
  };

  // Badges automáticos según specs
  const badges: { label: string; color?: string }[] = [];
  const specs = p.specs ?? {};
  if (specs.cri && Number(String(specs.cri).replace(/[^\d]/g, '')) >= 90) badges.push({ label: 'CRI 90+' });
  if (specs.ip) badges.push({ label: `IP${String(specs.ip).replace(/[^\d]/g, '') || specs.ip}` });
  if (p.lutron_compatible) badges.push({ label: 'Compatible Lutron', color: theme.brandTag.lutron });
  if (specs.dimmable || /dim/i.test(p.description ?? '')) badges.push({ label: 'Dimmable' });
  badges.push({ label: 'Garantía 1 año' });

  return (
    <Shell>
      <Seo
        title={`${p.name} (${productModel(p)}) · NULED`}
        description={`${p.short_description ?? p.name}. ${p.specs?.potencia ? `Potencia ${p.specs.potencia}.` : ''} ${p.specs?.voltaje ? `Voltaje ${p.specs.voltaje}.` : ''} Distribuidor NULED en México. Cotización 24h.`}
        canonical={`https://nuled.mx/producto/${encodeURIComponent(p.sku)}`}
        ogImage={main?.url}
        jsonLd={[
          productJsonLd({
            name: p.name,
            model: productModel(p),
            description: p.short_description ?? p.description,
            image: main?.url,
            msrp_mxn: p.msrp_mxn,
            brand_code: p.brand_code,
          }),
          breadcrumbJsonLd([
            { name: 'Inicio', url: 'https://nuled.mx/' },
            { name: 'Catálogo', url: 'https://nuled.mx/catalogo' },
            { name: p.name, url: `https://nuled.mx/producto/${encodeURIComponent(p.sku)}` },
          ]),
        ]}
      />

      <section style={{ maxWidth: theme.maxWidth, margin: '0 auto', padding: '24px 24px 8px' }}>
        <div style={{ fontSize: 12, color: theme.textMuted }}>
          <Link to="/" style={crumb}>Inicio</Link>
          {' / '}
          <Link to="/catalogo" style={crumb}>Catálogo</Link>
          {' / '}
          <span style={{ color: theme.brand }}>{p.brand_code === 'lutron' ? prettifyLutronName(p.name) : p.name}</span>
        </div>
      </section>

      <section className="product-detail-layout" style={{
        maxWidth: theme.maxWidth,
        margin: '0 auto',
        padding: '24px 24px 48px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        gap: 48,
      }}>
        {/* Galería con zoom */}
        <div className="product-detail-gallery">
          <div
            style={{
              border: `1px solid ${theme.border}`,
              aspectRatio: '1 / 1',
              backgroundColor: theme.bgImage,
              backgroundImage: main ? `url(${thumbUrl(main.url, 800)})` : undefined,
              backgroundSize: zoom ? '200%' : 'contain',
              backgroundPosition: zoom ? `${zoom.x}% ${zoom.y}%` : 'center',
              backgroundRepeat: 'no-repeat',
              cursor: zoom ? 'zoom-out' : 'zoom-in',
              transition: 'background-size 0.15s',
              touchAction: 'pan-y',
            }}
            onTouchStart={e => { swipeX.current = e.touches[0].clientX; }}
            onTouchEnd={e => {
              if (swipeX.current == null || imgs.length < 2) { swipeX.current = null; return; }
              const dx = e.changedTouches[0].clientX - swipeX.current;
              swipeX.current = null;
              if (Math.abs(dx) < 40) return;
              const n = imgs.length;
              setActiveImg(dx < 0 ? (visibleIndex + 1) % n : (visibleIndex - 1 + n) % n);
              setZoom(null);
            }}
            onMouseMove={e => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = ((e.clientX - rect.left) / rect.width) * 100;
              const y = ((e.clientY - rect.top) / rect.height) * 100;
              setZoom({ x, y });
            }}
            onMouseLeave={() => setZoom(null)}
          />
          {imgs.length > 1 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {imgs.map((img, i) => (
                <button
                  key={i}
                  onClick={() => { setActiveImg(i); setZoom(null); }}
                  aria-label={`Imagen ${i + 1}`}
                  style={{
                    width: 64,
                    height: 64,
                    border: `1px solid ${img.url === main?.url ? theme.brand : theme.border}`,
                    background: theme.bg,
                    cursor: 'pointer',
                    padding: 0,
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <img
                    src={thumbUrl(img.url, 120)}
                    alt={`Imagen ${i + 1}`}
                    loading="lazy"
                    decoding="async"
                    style={{
                      position: 'absolute', top: 0, left: 0,
                      width: '100%', height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info principal */}
        <div>
          <div style={{
            fontSize: 11,
            color: theme.textMuted,
            fontFamily: theme.font.mono,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}>
            Modelo: {productModel(p)}
          </div>
          <h1 style={{
            fontFamily: theme.font.head,
            fontSize: 30,
            fontWeight: 400,
            color: theme.brand,
            margin: '8px 0 16px',
            lineHeight: 1.2,
          }}>
            {p.brand_code === 'lutron' ? prettifyLutronName(p.name) : p.name}
            {selectedVariant?.finish && (
              <span style={{ color: theme.accentDark, fontSize: 22, marginLeft: 8 }}>
                · {selectedVariant.finish}
              </span>
            )}
          </h1>

          {/* Descripción larga: la del Excel Lutron traducida, o short_description si es NULED */}
          {(() => {
            const fullDesc = p.description;
            const showDesc = p.brand_code === 'lutron' && fullDesc
              ? translateLutronDesc(fullDesc)
              : (p.short_description || fullDesc);
            if (!showDesc) return null;
            return (
              <p style={{ color: theme.textMuted, fontSize: 14, lineHeight: 1.65, marginBottom: 16 }}>
                {showDesc}
              </p>
            );
          })()}

          {/* Badges automáticos */}
          {badges.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
              {badges.map(b => (
                <span key={b.label} style={{
                  fontSize: 10,
                  color: b.color ?? theme.brand,
                  border: `1px solid ${b.color ?? theme.border}`,
                  padding: '4px 10px',
                  borderRadius: 999,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  background: theme.bg,
                }}>
                  {b.label}
                </span>
              ))}
            </div>
          )}

          {/* Precio dual: PVP (público) + Costo distribuidor */}
          <PriceBlock
            usd={effectiveUsd}
            mxn={effectiveMxn}
            sku={effectiveSku}
            session={!!session}
            brandCode={p.brand_code}
          />

          {/* Variantes con stock */}
          {variants.length > 0 && (
            <VariantSelector
              variants={variants}
              selectedId={selectedVariantId}
              onSelect={selectVariant}
              stockOf={variantStock}
              availabilityFor={availabilityFor}
              leadTimeLabel={p.product_type?.startsWith('compose_') ? 'Entrega estimada: 6–8 semanas · confirma el plazo al cotizar' : undefined}
              onShowAll={() => setShowInventoryAll(true)}
            />
          )}

          {/* Sin variantes: banner de disponibilidad simple */}
          {variants.length === 0 && (
            <AvailabilityBanner
              available={variantStock(null)}
              availability={availabilityFor(null)}
              unit={p.unit ?? 'piezas'}
              leadTimeLabel={p.product_type?.startsWith('compose_') ? 'Entrega estimada: 6–8 semanas · confirma el plazo al cotizar' : undefined}
            />
          )}

          {(() => {
            const simulation = lightSimulation(p.sku, p.specs ?? {});
            if (simulation.mode === 'off') return null;
            const attrs = selectedVariant?.attributes ?? p.specs ?? {};
            const angle = Number(selectedVariant?.beam_angle_deg ?? String(attrs.angulo ?? '').replace('°', ''));
            const tone = Number(selectedVariant?.color_temperature_k ?? String(attrs.temperatura_color ?? attrs.temperatura_de_color ?? '').replace('K', ''));
            const finish = selectedVariant?.finish ?? String(attrs.acabado ?? '');
            if (!Number.isFinite(angle) || angle <= 0 || angle >= 90 || !Number.isFinite(tone) || tone < 1800 || tone > 6500) return null;
            const query = new URLSearchParams({angle: String(angle), tone: String(tone), finish, maxTilt: String(simulation.maxTilt)});
            return <details style={{border: '1px solid #ccd8d0', margin: '16px 0'}}>
              <summary style={{padding: '13px 15px', cursor: 'pointer', fontWeight: 600}}>Visualiza el tono y la apertura</summary>
              <iframe title="Simulador visual de la luminaria" src={`/light-preview.html?${query}`} loading="lazy" sandbox="allow-scripts" style={{width:'100%', height:490, border:0}} />
            </details>;
          })()}

          {/* Specs visibles upfront, sin tabs */}
          <h2 style={{fontSize:20, fontWeight:500, margin:'20px 0 8px'}}>Características técnicas</h2>
          <SpecsGrid specs={{...p.specs, ...selectedVariant?.attributes,
            ...(selectedVariant?.attributes?.temperatura_color ? {temperatura_de_color: selectedVariant.attributes.temperatura_color, temperatura_color: undefined} : {}),
            ...(selectedVariant?.color_temperature_k ? {temperatura_de_color: `${selectedVariant.color_temperature_k}K`} : {}),
            ...(selectedVariant?.beam_angle_deg ? {angulo: `${selectedVariant.beam_angle_deg}°`} : {}),
            ...(selectedVariant?.finish ? {acabado: selectedVariant.finish} : {}),
            ...(selectedVariant?.wattage_w != null ? {potencia: `${selectedVariant.wattage_w} W`} : {}),
            ...(selectedVariant?.voltage_v != null ? {voltaje: `${selectedVariant.voltage_v} V`} : {}),
            ...(selectedVariant?.cri != null ? {cri: `>${selectedVariant.cri}`} : {}),
            ...(selectedVariant?.ip_rating ? {ip: selectedVariant.ip_rating} : {}),
            ...(selectedVariant?.dim_method ? {sistema_control: selectedVariant.dim_method} : {})}} />

          {/* Calculadora */}
          {(effectiveUsd != null || effectiveMxn != null) && session && (
            <QtyCalculator usd={effectiveUsd} mxn={effectiveMxn} unit={p.unit ?? 'pieza'} brandCode={p.brand_code} />
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
            <AddToProject
              variant="primary"
              product={{
                product_id: p.id,
                sku: effectiveSku,
                name: p.name,
                brand_code: p.brand_code,
                msrp_mxn: effectiveMxn,
                msrp_usd: effectiveUsd,
                price_pending: pricePending,
                image_url: main?.url ?? null,
                unit: p.unit,
              }}
            />
            <a
              href={`https://wa.me/525549332582?text=${encodeURIComponent(`Hola NULED, me interesa el producto ${effectiveSku} — ${p.name}\n\n_Referencia web: ${getTrackingSessionId()}_`)}`}
              onClick={() => trackEvent('click_whatsapp', { sku: effectiveSku, name: p.name, source: 'product_detail' })}
              style={{
                background: theme.bg,
                color: theme.brand,
                padding: '14px 24px',
                fontSize: 13,
                fontWeight: 700,
                textDecoration: 'none',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                border: `1px solid ${theme.border}`,
              }}
            >
              Cotizar por WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Bloques siempre visibles */}
      <section style={{ maxWidth: theme.maxWidth, margin: '0 auto', padding: '0 24px 48px' }}>
        <SectionHeader>Compatibilidad</SectionHeader>
        <CompatTab product={p} />
      </section>

      <section style={{ maxWidth: theme.maxWidth, margin: '0 auto', padding: '0 24px 48px' }}>
        <SectionHeader>Aplicaciones</SectionHeader>
        <AppsTab tags={p.tags ?? []} />
      </section>

      <section style={{ maxWidth: theme.maxWidth, margin: '0 auto', padding: '0 24px 48px' }}>
        <SectionHeader>Documentos</SectionHeader>
        <DocsTab specs={p.specs ?? {}} sku={effectiveSku} productSku={p.sku} brandCode={p.brand_code} />
      </section>

      {/* Descripción larga */}
      {p.description && (
        <section style={{ maxWidth: 920, margin: '0 auto', padding: '0 24px 64px' }}>
          <div style={smallLabel}>Descripción</div>
          <h2 style={{
            fontFamily: theme.font.head,
            fontSize: 22,
            fontWeight: 500,
            color: theme.brand,
            margin: '8px 0 16px',
          }}>
            Sobre este producto
          </h2>
          {p.brand_code === 'lutron' ? (
            <div style={{ color: theme.textMuted, fontSize: 15, lineHeight: 1.7 }}>
              {translateLutronDesc(p.description)}
            </div>
          ) : (
            <div
              style={{ color: theme.textMuted, fontSize: 15, lineHeight: 1.7 }}
              dangerouslySetInnerHTML={{ __html: p.description }}
            />
          )}
        </section>
      )}

      {/* Modal de inventario por variante */}
      {showInventoryAll && (
        <VariantInventoryModal
          variants={variants}
          inventory={inventory}
          onClose={() => setShowInventoryAll(false)}
        />
      )}

      {/* Productos relacionados */}
      {related.length > 0 && (
        <section style={{
          background: theme.bgFooter,
          padding: '48px 24px',
        }}>
          <div style={{ maxWidth: theme.maxWidth, margin: '0 auto' }}>
            <div style={smallLabel}>Productos relacionados</div>
            <h2 style={{
              fontFamily: theme.font.head,
              fontSize: 22,
              fontWeight: 500,
              color: theme.brand,
              margin: '8px 0 24px',
            }}>
              También podría interesarte
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 16,
            }}>
              {related.map(r => <RelatedCard key={r.id} p={r} loggedIn={!!session} />)}
            </div>
          </div>
        </section>
      )}
    </Shell>
  );
}

// ============= Sub-componentes =============

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{
      fontFamily: theme.font.head,
      fontSize: 18,
      fontWeight: 500,
      color: theme.brand,
      margin: '0 0 16px',
      paddingBottom: 8,
      borderBottom: `1px solid ${theme.border}`,
      letterSpacing: 0.3,
    }}>
      {children}
    </h2>
  );
}

// Mapa de specs → label legible + emoji icon
const SPEC_META: Record<string, { label: string; icon: string }> = {
  peso:                  { label: 'Peso',                icon: '⚖' },
  tipo_luminaria:        { label: 'Tipo de luminaria',   icon: '💡' },
  tipo_led:              { label: 'Tipo de LED',         icon: '💎' },
  potencia:              { label: 'Potencia',            icon: '⚡' },
  voltaje:               { label: 'Voltaje',             icon: '🔌' },
  temperatura_de_color:  { label: 'Temperatura de color', icon: '🌡' },
  cct:                   { label: 'Temperatura de color', icon: '🌡' },
  flujo_luminoso:        { label: 'Flujo luminoso',      icon: '✨' },
  lumens:                { label: 'Flujo luminoso',      icon: '✨' },
  ip:                    { label: 'IP',                  icon: '🛡' },
  angulo:                { label: 'Ángulo de apertura',  icon: '📐' },
  cri:                   { label: 'CRI (índice color)',  icon: '🎨' },
  acabado:               { label: 'Acabado',             icon: '🎭' },
  finish:                { label: 'Acabado',             icon: '🎭' },
  dimensiones:           { label: 'Dimensiones',         icon: '📏' },
  tiempo_entrega:        { label: 'Tiempo de entrega',   icon: '⏱' },
  delivery:              { label: 'Tiempo de entrega',   icon: '⏱' },
  cantidad:              { label: 'Cantidad',            icon: '📦' },
  driver:                { label: 'Driver',              icon: '🔋' },
  dimmable:              { label: 'Atenuable',           icon: '🌗' },
  garantia:              { label: 'Garantía',            icon: '🏷' },
  certificacion:         { label: 'Certificación',       icon: '✅' },
};

function specLabelFor(key: string) {
  const k = key.toLowerCase();
  if (SPEC_META[k]) return SPEC_META[k];
  // Heurísticas
  if (/peso|weight/.test(k))            return { label: 'Peso',                icon: '⚖' };
  if (/potencia|watt|power/.test(k))    return { label: 'Potencia',            icon: '⚡' };
  if (/volt|voltaje/.test(k))           return { label: 'Voltaje',             icon: '🔌' };
  if (/temp|cct|kelvin/.test(k))        return { label: 'Temperatura de color', icon: '🌡' };
  if (/lumen|flujo/.test(k))            return { label: 'Flujo luminoso',      icon: '✨' };
  if (/ip\b|ingress/.test(k))           return { label: 'IP',                  icon: '🛡' };
  if (/angulo|angle|beam/.test(k))      return { label: 'Ángulo',              icon: '📐' };
  if (/cri|color rendering/.test(k))    return { label: 'CRI',                 icon: '🎨' };
  if (/acabado|finish|color/.test(k))   return { label: 'Acabado',             icon: '🎭' };
  if (/dimen|size/.test(k))             return { label: 'Dimensiones',         icon: '📏' };
  if (/entrega|delivery|lead/.test(k))  return { label: 'Tiempo de entrega',   icon: '⏱' };
  if (/dim|atenu/.test(k))              return { label: 'Atenuable',           icon: '🌗' };
  if (/garant|warranty/.test(k))        return { label: 'Garantía',            icon: '🏷' };
  // fallback humanize key
  return {
    label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    icon: '•',
  };
}

function SpecsGrid({ specs }: { specs: Record<string, unknown> }) {
  // El plazo comercial se muestra en el banner de disponibilidad de la variante.
  const entries = Object.entries(specs).filter(([k, v]) =>
    !k.startsWith('_')
    && k !== 'tiempo_de_entrega'
    && isPublicSpecKey(k)
    && v != null
    && String(v).trim() !== ''
  );
  if (!entries.length) return null;
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '0 24px',
      }}>
        {entries.map(([k, v]) => {
          const meta = specLabelFor(k);
          return (
            <div
              key={k}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '10px 0',
                borderBottom: `1px solid ${theme.border}`,
              }}
            >
              
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 11,
                  color: theme.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontWeight: 700,
                }}>
                  {meta.label}
                </div>
                <div style={{
                  fontSize: 14,
                  color: theme.brand,
                  fontWeight: 500,
                  marginTop: 2,
                  wordBreak: 'break-word',
                }}>
                  {String(v)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PriceBlock({
  usd, mxn, sku, session, brandCode,
}: {
  usd: number | null;
  mxn: number | null;
  sku: string;
  session: boolean;
  brandCode?: string | null;
}) {
  const { display, fmtUsd, fmtMxn, usdMxn, preferred } = useCurrency();
  const { customer } = useCustomer();

  if (!session) {
    return (
      <div style={priceCardStyle}>
        <div style={{ color: theme.brand, fontSize: 15, fontWeight: 500 }}>
          Inicia sesión para ver tu costo distribuidor
        </div>
        <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 6, lineHeight: 1.5 }}>
          Los distribuidores autorizados ven precio público de venta sugerido + costo según su tier.
        </div>
        <Link to="/login" style={{
          display: 'inline-block',
          marginTop: 14,
          background: theme.accent,
          color: theme.brand,
          padding: '10px 20px',
          fontSize: 13,
          fontWeight: 700,
          textDecoration: 'none',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}>
          Inicia sesión
        </Link>
      </div>
    );
  }

  if (usd == null && mxn == null) {
    return (
      <div style={priceCardStyle}>
        <div style={{ fontFamily: theme.font.head, fontSize: 22, color: theme.brand, fontWeight: 500 }}>
          Cotización por solicitud
        </div>
        <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>
          Este SKU se cotiza directo con tu agente.
        </div>
      </div>
    );
  }

  // PVP (precio sugerido al consumidor) — basado en USD (source of truth) o MXN legacy
  const pvp = display(usd, mxn);

  // Costo distribuidor: PVP × (1 - descuento_por_marca/100)
  const discount = discountForBrand(customer, brandCode);
  const tc = usdMxn ?? 18;

  const costUsd = usd != null ? usd * (1 - discount / 100) : null;
  const costMxn = costUsd != null ? costUsd * tc : (mxn != null ? mxn * (1 - discount / 100) : null);

  const costPrimary = preferred === 'USD'
    ? fmtUsd(costUsd ?? (mxn != null ? mxn * (1 - discount / 100) / tc : null))
    : fmtMxn(costMxn);
  const costSecondary = preferred === 'USD'
    ? fmtMxn(costMxn)
    : fmtUsd(costUsd ?? (mxn != null ? mxn * (1 - discount / 100) / tc : null));

  return (
    <div style={priceCardStyle}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'flex-start' }}>
        <div>
          <div style={{
            fontSize: 10, color: theme.textMuted,
            textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700,
          }}>
            Precio sugerido de venta
          </div>
          <div style={{
            fontFamily: theme.font.head, fontSize: 24, fontWeight: 700,
            color: theme.brand, marginTop: 4, lineHeight: 1.1,
          }}>
            {pvp.primary}
          </div>
          {pvp.secondary && (
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
              ≈ {pvp.secondary}
            </div>
          )}
          <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 6 }}>
            Precio público (PVP)
          </div>
        </div>

        <div style={{
          paddingLeft: 20,
          borderLeft: `2px solid ${theme.accent}`,
        }}>
          <div style={{
            fontSize: 10, color: theme.accentDark,
            textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700,
          }}>
            Tu costo distribuidor
          </div>
          <div style={{
            fontFamily: theme.font.head, fontSize: 24, fontWeight: 700,
            color: theme.accentDark, marginTop: 4, lineHeight: 1.1,
          }}>
            {costPrimary}
          </div>
          <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
            ≈ {costSecondary}
          </div>
          <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 6 }}>
            {customer?.tier_name ?? 'Tier'} · −{discount}% sobre PVP
          </div>
        </div>
      </div>

      <div style={{
        marginTop: 14,
        paddingTop: 12,
        borderTop: `1px solid ${theme.border}`,
        fontSize: 11,
        color: theme.textMuted,
        fontFamily: theme.font.mono,
      }}>
        SKU vigente: <strong style={{ color: theme.brand }}>{sku}</strong>
      </div>
    </div>
  );
}

function VariantSelector({
  variants, selectedId, onSelect, stockOf, availabilityFor, leadTimeLabel, onShowAll,
}: {
  variants: Variant[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  stockOf: (variantId: string | null) => number;
  availabilityFor: (variantId: string | null) => Availability | null;
  leadTimeLabel?: string;
  onShowAll: () => void;
}) {
  const byTemp   = unique(variants.map(v => v.color_temperature_k).filter((x): x is number => !!x)).sort((a,b) => a-b);
  const byBeam   = unique(variants.map(v => v.beam_angle_deg).filter((x): x is number => !!x)).sort((a,b) => a-b);
  const byFinish = unique(variants.map(v => v.finish).filter((x): x is string => !!x));
  const byDim    = unique(variants.map(v => v.dim_method).filter((x): x is string => !!x));

  // Ejes desde attributes (ej. dimensiones/longitud) que NO están cubiertos por columnas estructuradas.
  const av = (v: Variant | undefined, k: string): string => String(((v?.attributes ?? {}) as Record<string, unknown>)[k] ?? '').trim();
  const shown = { finish: byFinish.length > 0, temp: byTemp.length > 0, beam: byBeam.length > 0, watt: false, dim: byDim.length > 0 };
  const overlaps = (key: string): boolean => {
    const k = key.toLowerCase();
    if (shown.finish && /(acabado|finish|color)/.test(k)) return true;
    if (shown.temp && /(cct|temperatura)/.test(k)) return true;
    if (shown.beam && /(angul|apertur)/.test(k)) return true;
    if (shown.watt && /(potenc|watt|vatio)/.test(k)) return true;
    if (shown.dim && /(dimeo|atenu|sistema)/.test(k)) return true;
    return false;
  };
  // Solo ejes canónicos extra (no cubiertos por columnas). Excluye legacy 'line'/'packaging'/etc.
  const EXTRA_AXES = new Set(['dimensiones', 'perfil', 'optica', 'rango_optico', 'longitud', 'largo', 'apertura']);
  const attrKeysAll = unique(variants.flatMap(v => Object.keys((v.attributes ?? {}) as object)));
  const attrAxes = attrKeysAll
    .filter(k => EXTRA_AXES.has(k.toLowerCase()) && !overlaps(k))
    .map(k => ({ key: k, values: unique(variants.map(v => av(v, k)).filter(Boolean)) }))
    .filter(a => a.values.length > 0);
  const attrLabel = (k: string): string => {
    const kk = k.toLowerCase();
    if (kk.includes('dimen')) return '📏 Dimensión';
    if (kk.includes('longitud') || kk.includes('largo')) return '📏 Longitud';
    if (kk.includes('optic') || kk.includes('apertur')) return 'Óptica';
    if (kk.includes('voltaj')) return 'Voltaje';
    return '🔧 ' + k.charAt(0).toUpperCase() + k.slice(1);
  };
  const attrChip = (k: string, val: string): string => {
    if (k.toLowerCase().includes('dimen')) {
      const nums = val.match(/[\d.]+/g);
      if (nums && nums.length) {
        const last = parseFloat(nums[nums.length - 1]);
        if (!isNaN(last)) return (last >= 50 ? +(last / 100).toFixed(2) : last) + ' m';
      }
    }
    return val;
  };

  const sel = variants.find(v => v.id === selectedId);
  const selStock = sel ? stockOf(sel.id) : 0;

  type Axis = 'color_temperature_k' | 'beam_angle_deg' | 'finish' | 'wattage_w' | 'dim_method';

  const matchesOthers = (v: Variant, axis: string): boolean =>
       (axis !== 'color_temperature_k' ? v.color_temperature_k === sel?.color_temperature_k : true)
    && (axis !== 'beam_angle_deg'      ? v.beam_angle_deg      === sel?.beam_angle_deg      : true)
    && (axis !== 'finish'              ? v.finish              === sel?.finish              : true)
    && (axis !== 'wattage_w'           ? v.wattage_w           === sel?.wattage_w           : true)
    && (axis !== 'dim_method'          ? v.dim_method          === sel?.dim_method          : true)
    && attrAxes.every(a => (axis === `attr:${a.key}` ? true : av(v, a.key) === av(sel, a.key)));

  const setAttr = (key: string, value: string) => {
    const candidate = variants.find(v => av(v, key) === value && matchesOthers(v, `attr:${key}`)) ?? variants.find(v => av(v, key) === value);
    if (candidate) onSelect(candidate.id);
  };
  const stockForAttr = (key: string, value: string): number => {
    const candidate = variants.find(v => av(v, key) === value && matchesOthers(v, `attr:${key}`));
    return candidate ? stockOf(candidate.id) : 0;
  };

  const setAxis = (axis: Axis, value: number | string) => {
    const candidate = variants.find(v => v[axis] === value && matchesOthers(v, axis))
      ?? variants.find(v => v[axis] === value);
    if (candidate) onSelect(candidate.id);
  };

  const stockForChip = (axis: Axis, value: number | string): number => {
    const candidate = variants.find(v => v[axis] === value && matchesOthers(v, axis));
    return candidate ? stockOf(candidate.id) : 0;
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        fontSize: 14, fontFamily: theme.font.head,
        color: theme.brand, fontWeight: 600,
        marginBottom: 14, letterSpacing: 0.3,
      }}>
        Tu configuración
      </div>

      {byFinish.length > 0 && (
        <VariantAxis label="Acabado">
          {byFinish.map(f => (
            <FinishChip
              key={f}
              finish={f}
              active={sel?.finish === f}
              stock={stockForChip('finish', f)}
              onClick={() => setAxis('finish', f)}
            />
          ))}
        </VariantAxis>
      )}
      {byTemp.length > 0 && (
        <VariantAxis label="Temperatura de color">
          {byTemp.map(t => (
            <VariantChip
              key={t}
              active={sel?.color_temperature_k === t}
              stock={stockForChip('color_temperature_k', t)}
              onClick={() => setAxis('color_temperature_k', t)}
            >{t}K</VariantChip>
          ))}
        </VariantAxis>
      )}
      {byBeam.length > 0 && (
        <VariantAxis label="Ángulo de apertura">
          {byBeam.map(b => (
            <VariantChip
              key={b}
              active={sel?.beam_angle_deg === b}
              stock={stockForChip('beam_angle_deg', b)}
              onClick={() => setAxis('beam_angle_deg', b)}
            >{b}°</VariantChip>
          ))}
        </VariantAxis>
      )}
      {byDim.length > 0 && (
        <VariantAxis label="Sistema de control">
          {byDim.map(d => (
            <VariantChip
              key={d}
              active={sel?.dim_method === d}
              stock={stockForChip('dim_method', d)}
              onClick={() => setAxis('dim_method', d)}
            >{d}</VariantChip>
          ))}
        </VariantAxis>
      )}

      {attrAxes.map(a => (
        <VariantAxis key={a.key} label={attrLabel(a.key)}>
          {a.values.map(val => (
            <VariantChip
              key={val}
              active={av(sel, a.key) === val}
              stock={stockForAttr(a.key, val)}
              onClick={() => setAttr(a.key, val)}
            >{attrChip(a.key, val)}</VariantChip>
          ))}
        </VariantAxis>
      ))}

      {sel && <div className="product-configuration-summary">Tu configuración: {Array.from(new Set([
        sel.finish, sel.color_temperature_k ? `${sel.color_temperature_k}K` : null,
        sel.beam_angle_deg ? `${sel.beam_angle_deg}°` : null, sel.dim_method,
        ...Object.entries(sel.attributes ?? {})
          .filter(([key, value]) => isPublicSpecKey(key) && value != null && value !== false && String(value).trim() !== '')
          .map(([, value]) => String(value))
      ].filter(Boolean))).join(' · ')}</div>}
      {/* Disponibilidad real de la combinación seleccionada */}
      <AvailabilityBanner
        available={selStock}
        availability={availabilityFor(sel?.id ?? null)}
        unit="piezas"
        leadTimeLabel={leadTimeLabel}
        rightAction={
          <button
            onClick={onShowAll}
            style={{
              background: 'transparent',
              border: `1px solid ${theme.border}`,
              color: theme.brand,
              padding: '6px 12px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              cursor: 'pointer',
              fontFamily: theme.font.sans,
            }}
          >
            Ver todas →
          </button>
        }
      />
    </div>
  );
}

/**
 * Banner unificado de disponibilidad con 3 estados:
 *  - in_stock: verde, "✓ Disponible: N piezas"
 *  - made_to_order: naranja, "📦 Bajo pedido · entrega ~X días"
 *  - restocking: amarillo, "🔄 En reposición · ~X días"
 */
function AvailabilityBanner({
  available, availability, unit, leadTimeLabel, rightAction,
}: {
  available: number;
  availability: Availability | null;
  unit: string;
  leadTimeLabel?: string;
  rightAction?: React.ReactNode;
}) {
  const configuredStatus = availability?.availability_status;
  const status = configuredStatus === 'discontinued' ? 'discontinued'
    : available > 0 ? 'in_stock'
    : configuredStatus === 'restocking' ? 'restocking' : 'made_to_order';
  const lead = availability?.lead_time_days;
  const delivery = leadTimeLabel ?? (lead != null && lead > 0
    ? `Entrega estimada: ${lead} días · confirma el plazo al cotizar`
    : 'Consulta disponibilidad y plazo de entrega al cotizar');

  const cfg = {
    in_stock: {
      color: theme.accentDark,
      bg: 'rgba(87,255,154,0.06)',
      icon: '✓',
      label: 'Disponible',
      detail: `${available} ${unit} en bodega · listo para envío`,
    },
    made_to_order: {
      color: theme.warn,
      bg: 'rgba(245,158,11,0.06)',
      icon: '📦',
      label: 'Bajo pedido',
      detail: delivery,
    },
    restocking: {
      color: theme.warn,
      bg: 'rgba(245,158,11,0.06)',
      icon: '🔄',
      label: 'En reposición',
      detail: delivery,
    },
    discontinued: {
      color: theme.err,
      bg: 'rgba(220,38,38,0.06)',
      icon: '✗',
      label: 'Descontinuado',
      detail: 'Ya no se vende. Contacta a tu agente para alternativas.',
    },
  }[status];

  return (
    <div style={{
      marginTop: 16,
      marginBottom: 16,
      padding: '12px 14px',
      background: cfg.bg,
      border: `1px solid ${theme.border}`,
      borderLeft: `4px solid ${cfg.color}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
    }}>
      <div style={{ fontSize: 14 }}>
        <span style={{ marginRight: 8, fontSize: 16 }}>{cfg.icon}</span>
        <strong style={{ color: cfg.color, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 12 }}>
          {cfg.label}
        </strong>
        {status === 'in_stock' && (
          <span style={{
            marginLeft: 10,
            fontFamily: theme.font.head,
            fontSize: 18,
            fontWeight: 700,
            color: theme.brand,
          }}>{available}</span>
        )}
        <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
          {cfg.detail}
        </div>
        {status !== 'in_stock' && status !== 'discontinued' && (
          <div style={{ fontSize: 11, color: theme.brand, marginTop: 4, fontWeight: 600 }}>
            ✓ Puedes cotizar y ordenar este SKU — el agente te confirma fecha exacta
          </div>
        )}
      </div>
      {rightAction}
    </div>
  );
}

function VariantInventoryModal({
  variants, inventory, onClose,
}: {
  variants: Variant[];
  inventory: InventoryRow[];
  onClose: () => void;
}) {
  // Por cada variante, sumar on_hand - reserved
  const stockByVariant = new Map<string, { onHand: number; reserved: number; available: number }>();
  for (const v of variants) {
    const rows = inventory.filter(r => r.variant_id === v.id);
    const onHand = rows.reduce((s, r) => s + (r.on_hand ?? 0), 0);
    const reserved = rows.reduce((s, r) => s + (r.reserved ?? 0), 0);
    stockByVariant.set(v.id, { onHand, reserved, available: Math.max(0, onHand - reserved) });
  }

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        padding: 24,
        width: '100%',
        maxWidth: 720,
        maxHeight: '85vh',
        overflowY: 'auto',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}>
          <h2 style={{
            fontFamily: theme.font.head,
            fontSize: 22,
            fontWeight: 500,
            color: theme.brand,
            margin: 0,
          }}>
            Existencias por variante
          </h2>
          <button onClick={onClose} style={{
            background: 'transparent',
            border: 'none',
            fontSize: 24,
            cursor: 'pointer',
            color: theme.textMuted,
          }}>×</button>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: theme.bgSoft }}>
              <th style={modalThStyle}>SKU</th>
              <th style={modalThStyle}>Configuración</th>
              <th style={{ ...modalThStyle, textAlign: 'right' }}>En mano</th>
              <th style={{ ...modalThStyle, textAlign: 'right' }}>Reservado</th>
              <th style={{ ...modalThStyle, textAlign: 'right' }}>Disponible</th>
            </tr>
          </thead>
          <tbody>
            {variants.map(v => {
              const st = stockByVariant.get(v.id) ?? { onHand: 0, reserved: 0, available: 0 };
              const config = [
                v.color_temperature_k && `${v.color_temperature_k}K`,
                v.beam_angle_deg && `${v.beam_angle_deg}°`,
                v.finish && capitalize(v.finish),
                v.wattage_w && `${v.wattage_w}W`,
                v.dim_method,
              ].filter(Boolean).join(' · ');
              return (
                <tr key={v.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <td style={{ padding: '10px 12px', fontFamily: theme.font.mono, fontSize: 11, color: theme.textMuted }}>
                    {v.full_sku ?? v.variant_sku ?? '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: theme.brand }}>{config}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>{st.onHand}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: theme.textMuted }}>{st.reserved}</td>
                  <td style={{
                    padding: '10px 12px', textAlign: 'right',
                    fontWeight: 700,
                    color: st.available > 0 ? theme.accentDark : theme.err,
                  }}>
                    {st.available}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 14 }}>
          Disponible = En mano − Reservado. Stock 0 indica bajo pedido (lead time del proveedor aplica).
        </div>
      </div>
    </div>
  );
}

const modalThStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: theme.textMuted,
  borderBottom: `1px solid ${theme.border}`,
};

function VariantAxis({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: 11, color: theme.textMuted,
        textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, marginBottom: 6,
      }}>
        {label}
      </div>
      <div className="product-variant-options" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{children}</div>
    </div>
  );
}

function VariantChip({
  active, onClick, children, stock,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  stock?: number;
}) {
  const outOfStock = stock !== undefined && stock <= 0;
  return (
    <button className="product-variant-choice" aria-pressed={active} onClick={onClick} style={{
      background: active ? theme.brand : theme.bg,
      color: active ? theme.textOnBrand : (outOfStock ? theme.textMuted : theme.brand),
      border: `1.5px solid ${active ? theme.accent : theme.border}`,
      padding: '8px 14px',
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: theme.font.sans,
      minWidth: 60,
      position: 'relative',
      opacity: outOfStock && !active ? 0.6 : 1,
    }}>
      {children}
      {stock !== undefined && stock > 0 && !active && (
        <span style={{
          marginLeft: 6,
          fontSize: 9,
          color: theme.accentDark,
          fontWeight: 700,
        }}>
          ·{stock}
        </span>
      )}
    </button>
  );
}

// Mapping finish name (capitalized) → hex color para swatches visuales
const FINISH_HEX: Record<string, string> = {
  'Blanco': '#ffffff', 'Negro': '#1b1b1b', 'White': '#F4F4F0', 'Snow': '#FAFAF7', 'Biscuit': '#E8DEC7', 'Ivory': '#EFE5C9',
  'Light Almond': '#D5C9A8', 'Almond': '#C8B791', 'Brown': '#5A4733', 'Black': '#1B1B1B',
  'Gray': '#7A7A7A', 'Slate': '#5C6470', 'Sand': '#B8A689', 'Clay': '#9C7B5C',
  'Midnight': '#1F2933', 'Taupe': '#8B7A66', 'Cashmere': '#B5A78C', 'Espresso': '#3A2A20',
  'Desert': '#C5B498', 'Mist': '#A5B0BC', 'Plum Black': '#2A1F2A', 'Palladium': '#A8A6A0',
  'Lunar Gray': '#A0A0A0', 'Satin Nickel': '#B8B5A8', 'Satin Chrome': '#C8C8C0',
  'Bright Nickel': '#D4C8A0', 'Terracotta': '#A55A40', 'Copper': '#B87333',
  'Graphite Pearl': '#3A3F45', 'Warm Gold': '#C9A572', 'Hot': '#D03A2A',
  'Plum': '#5A2E4F', 'Raw White': '#EBE8E0', 'Silver Rose': '#A89090', 'Glass': '#E5E7EB',
  'Birchwood': '#A88770', 'Sand Dune': '#B89F75', 'Truffle': '#5C4632',
  'Hybrid Nickel': '#9E9890', 'Clear White': '#F8F8F5', 'Clear Black': '#0E0E0E',
  'Carbon': '#3A3A3A', 'Titanium': '#9A9590', 'Ash': '#8E8E84',
};

function FinishChip({ finish, active, onClick, stock }: {
  finish: string;
  active: boolean;
  onClick: () => void;
  stock?: number;
}) {
  const outOfStock = stock !== undefined && stock <= 0;
  const hex = FINISH_HEX[finish] || '#C0C0C0';
  // detectar si es color claro para borde
  const isLight = ['Blanco','White','Snow','Biscuit','Ivory','Glass','Clear White','Raw White'].includes(finish);

  return (
    <button className="product-variant-choice" aria-pressed={active} onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      background: active ? theme.brand : theme.bg,
      color: active ? theme.textOnBrand : (outOfStock ? theme.textMuted : theme.brand),
      border: `1.5px solid ${active ? theme.accent : theme.border}`,
      padding: '6px 12px 6px 8px',
      fontSize: 13, fontWeight: 600,
      cursor: 'pointer',
      fontFamily: theme.font.sans,
      opacity: outOfStock && !active ? 0.6 : 1,
    }}>
      <span style={{
        display: 'inline-block',
        width: 18, height: 18,
        borderRadius: '50%',
        background: hex,
        border: isLight ? `1px solid ${theme.border}` : 'none',
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)',
      }} />
      <span>{finish}</span>
      {stock !== undefined && stock > 0 && !active && (
        <span style={{ fontSize: 9, color: theme.accentDark, fontWeight: 700 }}>·{stock}</span>
      )}
    </button>
  );
}

function unique<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function CompatTab({ product }: { product: ProductPublic }) {
  const isLutron = product.brand_code === 'lutron';
  const specs: any = product.specs ?? {};
  const dimMethod = specs.dim_method as string | undefined; // ELV/MLV/TRIAC/0-10V/DALI
  const controlType = specs.control_type as string | undefined; // dimmer/switch/keypad/etc
  const isController = ['dimmer','switch','keypad','remote','hub','fan_control'].includes(controlType || '');

  // ============ PRODUCTO LUTRON: hablamos de qué luminarias NULED son compatibles ============
  if (isLutron) {
    // Mapear dim_method Lutron a tipos de luminaria NULED compatibles
    const compatMap: Record<string, { label: string; q: string; desc: string }[]> = {
      'TRIAC': [
        { label: 'LEDs dimables 127V', q: '127v+dim', desc: 'Drivers TRIAC integrados' },
        { label: 'Mangueras y tiras LED', q: 'manguera+led', desc: 'Con driver TRIAC compatible' },
      ],
      'ELV': [
        { label: 'LEDs con driver ELV', q: 'elv+led', desc: 'Mejor control de bajos' },
        { label: 'Drivers MLV/ELV', q: 'driver+elv', desc: '' },
      ],
      'MLV': [
        { label: 'Transformadores magnéticos', q: 'mlv', desc: 'Halógenas y LED MLV' },
      ],
      '0-10V': [
        { label: 'LED comercial 0-10V', q: '0-10v', desc: 'Comercial / oficinas' },
        { label: 'Riel magnético 48V', q: 'riel+48v', desc: 'Drivers 0-10V opcionales' },
      ],
      'DALI': [
        { label: 'LEDs con driver DALI', q: 'dali', desc: 'Comercial avanzado' },
      ],
    };

    const compatList = dimMethod ? compatMap[dimMethod] ?? [] : [];

    return (
      <div style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
        <div style={{
          padding: 16,
          border: `1px solid ${theme.border}`,
          borderLeft: `4px solid ${theme.brandTag.lutron}`,
        }}>
          <div style={{ fontWeight: 700, color: theme.brand, fontSize: 15 }}>
            {isController ? 'Compatible con luminarias NULED' : 'Producto Lutron'}
          </div>
          <div style={{ color: theme.textMuted, fontSize: 13, marginTop: 6, lineHeight: 1.6 }}>
            {isController ? (
              dimMethod ? (
                <>
                  Este {controlType === 'dimmer' ? 'atenuador' : controlType === 'keypad' ? 'keypad' : controlType === 'switch' ? 'switch' : 'control'} Lutron usa <strong style={{ color: theme.brand }}>{dimMethod}</strong> — diseñado para luminarias NULED con driver de ese tipo.
                  Si dudas qué luminaria comprar con este control, contáctanos y te recomendamos el match exacto.
                </>
              ) : (
                <>Este control Lutron es compatible con luminarias NULED — contáctanos para confirmar el match según tu proyecto y tipo de carga (LED, ELV, MLV, 0-10V, DALI).</>
              )
            ) : (
              <>Este producto Lutron complementa cualquier sistema. Si tu proyecto incluye luminarias NULED, te ayudamos a integrar todo en una sola cotización.</>
            )}
          </div>

          {compatList.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 8 }}>
                Luminarias NULED compatibles con este control:
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {compatList.map(c => (
                  <Link
                    key={c.label}
                    to={`/catalogo?brand=nuled&q=${encodeURIComponent(c.q)}`}
                    style={pillLink}
                  >
                    {c.label} →
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link to="/catalogo?brand=nuled" style={{ ...pillLink, background: theme.accent, color: theme.brand, fontWeight: 700 }}>
              Ver catálogo NULED →
            </Link>
            <a href={`https://wa.me/525549332582?text=${encodeURIComponent(`Hola, ¿qué luminaria NULED puedo combinar con ${product.name} (${product.sku})?`)}`} style={pillLink}>
              Preguntar al especialista
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ============ PRODUCTO NULED: hablamos de qué sistemas Lutron son compatibles ============
  const compatible = product.lutron_compatible;
  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
      <div style={{
        padding: 16,
        border: `1px solid ${theme.border}`,
        borderLeft: `4px solid ${compatible ? theme.brandTag.lutron : theme.border}`,
      }}>
        <div style={{ fontWeight: 700, color: theme.brand, fontSize: 15 }}>
          {compatible ? 'Compatible con Lutron' : 'Sin compatibilidad Lutron registrada'}
        </div>
        <div style={{ color: theme.textMuted, fontSize: 13, marginTop: 6, lineHeight: 1.6 }}>
          {compatible
            ? 'Esta luminaria está probada para integrar con sistemas de control Lutron — Caséta, RadioRA 3, Maestro, HomeWorks y Vive. Contáctanos para confirmar el dimmer correcto.'
            : 'Si tu proyecto requiere atenuación o control Lutron, contáctanos: te recomendamos una luminaria NULED probada con el sistema que necesitas.'}
        </div>
        {compatible && (
          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link to="/lutron/caseta" style={pillLink}>Ver Caséta</Link>
            <Link to="/lutron/radiora3" style={pillLink}>Ver RadioRA 3</Link>
            <Link to="/lutron/maestro" style={pillLink}>Ver Maestro</Link>
            <Link to="/lutron/homeworks" style={pillLink}>Ver HomeWorks</Link>
          </div>
        )}
      </div>
    </div>
  );
}

function AppsTab({ tags }: { tags: string[] }) {
  // Detecta aplicaciones desde tags
  const apps: { name: string; emoji: string; reason: string }[] = [];
  const tagSet = tags.map(t => t.toLowerCase()).join(' ');
  if (/exterior/.test(tagSet)) apps.push({ name: 'Exterior', emoji: '🌳', reason: 'Para espacios exteriores. Confirma el grado IP y las condiciones de instalación de la variante elegida.' });
  if (/tira|manguera/.test(tagSet)) apps.push({ name: 'Arquitectónica', emoji: '🏛', reason: 'Iluminación lineal continua para cornisas, gabinetes, escaleras.' });
  if (/downlight|empotrado/.test(tagSet)) apps.push({ name: 'Residencial', emoji: '🏠', reason: 'Iluminación general en techos.' });
  if (/proyector|track|riel/.test(tagSet)) apps.push({ name: 'Comercial', emoji: '🛍', reason: 'Iluminación dirigida en retail, galerías, museos.' });
  if (/driver|fuente/.test(tagSet)) apps.push({ name: 'Alimentación LED', emoji: '🔌', reason: 'Selecciona la fuente o driver según voltaje, potencia y sistema de atenuación del proyecto.' });

  if (!apps.length) {
    return <div style={emptyTab}>Aplicaciones no detectadas automáticamente. Contáctanos para casos de uso específicos.</div>;
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: 16,
    }}>
      {apps.map(a => (
        <div key={a.name} style={{
          padding: 20,
          border: `1px solid ${theme.border}`,
          background: theme.bg,
        }}>
          <div style={{ fontSize: 32 }}>{a.emoji}</div>
          <div style={{
            fontFamily: theme.font.head,
            fontSize: 16,
            fontWeight: 600,
            color: theme.brand,
            marginTop: 8,
          }}>
            {a.name}
          </div>
          <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 6, lineHeight: 1.5 }}>
            {a.reason}
          </div>
        </div>
      ))}
    </div>
  );
}

function DocsTab({ specs, sku, productSku, brandCode }: { specs: Record<string, string>; sku: string; productSku: string; brandCode?: string | null }) {
  const ficha = specs.ficha_tecnica || specs.fichatecnica;
  const isLutron = brandCode === 'lutron';
  const lutronUrl = isLutron ? lutronSpecUrl(sku) : null;
  const lutronSearch = isLutron ? lutronSearchUrl(sku) : null;

  return (
    <div style={{ display: 'grid', gap: 12, maxWidth: 540 }}>
      {ficha && <DocItem label="Ficha técnica" file={String(ficha)} />}

      {isLutron && lutronUrl && (
        <a
          href={lutronUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: 16,
            border: `1px solid ${theme.border}`,
            textDecoration: 'none',
            color: theme.brand,
            background: theme.bg,
          }}
        >
          <div style={{
            width: 40, height: 40,
            background: theme.accentSoft,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: theme.brand,
            fontSize: 20,
          }}>📄</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Ficha técnica oficial Lutron</div>
            <div style={{ fontSize: 12, color: theme.textMuted }}>
              lutron.com · SKU <code style={{ fontFamily: theme.font.mono }}>{sku}</code>
            </div>
          </div>
          <div style={{ fontSize: 12, color: theme.accentDark, fontWeight: 700 }}>↗</div>
        </a>
      )}

      {isLutron && lutronSearch && (
        <a
          href={lutronSearch}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 12,
            color: theme.textMuted,
            textDecoration: 'none',
            padding: '8px 12px',
            textAlign: 'center',
          }}
        >
          ¿No se carga la ficha? Buscar <strong style={{ fontFamily: theme.font.mono }}>{sku}</strong> en lutron.com ↗
        </a>
      )}

      {!isLutron && (
        <a
          href={`/producto/${encodeURIComponent(productSku)}/ficha`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: 16,
            border: `1px solid ${theme.border}`, textDecoration: 'none',
            color: theme.brand, background: theme.bg,
          }}
        >
          <div style={{
            width: 40, height: 40, background: theme.accentSoft,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: theme.brand, fontSize: 20,
          }}>📄</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Ver ficha técnica</div>
            <div style={{ fontSize: 12, color: theme.textMuted }}>Especificaciones, dimensiones y descarga en PDF</div>
          </div>
          <div style={{ fontSize: 12, color: theme.accentDark, fontWeight: 700 }}>↗</div>
        </a>
      )}
    </div>
  );
}

function DocItem({ label, file }: { label: string; file: string }) {
  return (
    <a
      href={`#`}
      onClick={e => { e.preventDefault(); alert(`Documento ${file} disponible al cotizar.`); }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 16,
        border: `1px solid ${theme.border}`,
        textDecoration: 'none',
        color: theme.brand,
      }}
    >
      <div style={{
        width: 40, height: 40,
        background: theme.accentSoft,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: theme.brand,
        fontSize: 20,
      }}>📄</div>
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
        <div style={{ fontSize: 12, color: theme.textMuted, fontFamily: theme.font.mono }}>{file}</div>
      </div>
    </a>
  );
}

function QtyCalculator({ usd, mxn, unit, brandCode }: { usd: number | null; mxn: number | null; unit: string; brandCode?: string | null }) {
  const [qty, setQty] = useState(1);
  const { customer } = useCustomer();
  const { display, usdMxn, fmtUsd, fmtMxn, preferred } = useCurrency();
  const discount = discountForBrand(customer, brandCode);
  const tc = usdMxn ?? 18;

  const unitUsd = usd ?? (mxn != null ? mxn / tc : null);
  const unitCostUsd = unitUsd != null ? unitUsd * (1 - discount / 100) : null;
  const totalCostUsd = unitCostUsd != null ? unitCostUsd * qty : null;
  const totalCostMxn = totalCostUsd != null ? totalCostUsd * tc : null;

  const totalPrimary = preferred === 'USD' ? fmtUsd(totalCostUsd) : fmtMxn(totalCostMxn);

  return (
    <div style={{ marginTop: 16, marginBottom: 16, padding: 14, background: theme.bgFooter, border: `1px solid ${theme.border}` }}>
      <div style={smallLabel}>📊 Calcular pedido</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
        <input
          type="number"
          min={1}
          value={qty}
          onChange={e => setQty(Math.max(1, parseInt(e.target.value) || 1))}
          style={{
            width: 100,
            padding: '10px 12px',
            border: `1px solid ${theme.border}`,
            fontSize: 14,
            color: theme.brand,
            fontFamily: theme.font.sans,
            outline: 'none',
            background: theme.bg,
          }}
        />
        <span style={{ color: theme.textMuted, fontSize: 13 }}>× {unit}</span>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
            Tu costo total
          </div>
          <div style={{ color: theme.accentDark, fontFamily: theme.font.head, fontWeight: 700, fontSize: 20 }}>
            {totalPrimary}
          </div>
        </div>
      </div>
      {display(unitUsd, mxn).primary && (
        <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 6 }}>
          PVP unitario: {display(unitUsd, mxn).primary} · Tu costo unitario: {preferred === 'USD' ? fmtUsd(unitCostUsd) : fmtMxn(unitCostUsd != null ? unitCostUsd * tc : null)}
        </div>
      )}
    </div>
  );
}

function RelatedCard({ p, loggedIn }: { p: ProductPublic; loggedIn: boolean }) {
  const img = p.images?.find(i => i.is_thumbnail) ?? p.images?.[0];
  const { display } = useCurrency();
  const price = display(p.msrp_usd, p.msrp_mxn);
  return (
    <Link to={`/producto/${encodeURIComponent(p.sku)}`} style={{
      background: theme.bg,
      border: `1px solid ${theme.border}`,
      textDecoration: 'none',
      color: theme.text,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        aspectRatio: '1 / 1',
        backgroundColor: theme.bgImage,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {img && (
          <img
            src={thumbUrl(img.url, 400)}
            alt={p.name}
            loading="lazy"
            decoding="async"
            style={{
              position: 'absolute', top: 0, left: 0,
              width: '100%', height: '100%',
              objectFit: 'contain',
            }}
          />
        )}
      </div>
      <div style={{ padding: 12, borderTop: `1px solid ${theme.border}` }}>
        <div style={{ fontSize: 13, color: theme.brand, fontWeight: 500, lineHeight: 1.3 }}>{p.name}</div>
        <div style={{ marginTop: 6, fontSize: 12 }}>
          {loggedIn && (p.msrp_usd != null || p.msrp_mxn != null) ? (
            <>
              <span style={{ fontWeight: 700, color: theme.brand }}>{price.primary}</span>
              {price.secondary && (
                <span style={{ color: theme.textMuted, marginLeft: 6, fontSize: 10 }}>
                  ≈ {price.secondary}
                </span>
              )}
            </>
          ) : (
            <span style={{ color: theme.textMuted, fontStyle: 'italic' }}>Inicia sesión para precio</span>
          )}
        </div>
      </div>
    </Link>
  );
}

function LoadingSkeleton() {
  return (
    <section style={{
      maxWidth: theme.maxWidth,
      margin: '0 auto',
      padding: '40px 24px',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
      gap: 48,
    }}>
      <div style={{ ...skeletonStyle, aspectRatio: '1 / 1' }} />
      <div>
        <div style={{ ...skeletonStyle, height: 14, width: 120, marginBottom: 12 }} />
        <div style={{ ...skeletonStyle, height: 28, width: '80%', marginBottom: 16 }} />
        <div style={{ ...skeletonStyle, height: 14, width: '100%', marginBottom: 8 }} />
        <div style={{ ...skeletonStyle, height: 14, width: '70%', marginBottom: 24 }} />
        <div style={{ ...skeletonStyle, height: 80, width: '100%' }} />
      </div>
    </section>
  );
}

const priceCardStyle: React.CSSProperties = {
  padding: 18,
  background: theme.bgFooter,
  border: `1px solid ${theme.border}`,
  borderLeft: `4px solid ${theme.accent}`,
  marginBottom: 20,
};

const skeletonStyle: React.CSSProperties = {
  background: 'linear-gradient(90deg, #F0F0F0 0%, #F8F8F8 50%, #F0F0F0 100%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.4s infinite linear',
  borderRadius: 2,
};

const crumb: React.CSSProperties = {
  color: theme.textMuted,
  textDecoration: 'none',
};

const smallLabel: React.CSSProperties = {
  fontSize: 11,
  color: theme.textMuted,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  fontWeight: 700,
};

const emptyTab: React.CSSProperties = {
  padding: 24,
  border: `1px dashed ${theme.border}`,
  color: theme.textMuted,
  fontSize: 14,
  textAlign: 'center',
};

const pillLink: React.CSSProperties = {
  display: 'inline-block',
  padding: '6px 12px',
  border: `1px solid ${theme.border}`,
  background: theme.bg,
  color: theme.brand,
  fontSize: 12,
  fontWeight: 600,
  textDecoration: 'none',
  borderRadius: 999,
};

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ background: theme.bg, color: theme.text, fontFamily: theme.font.sans }}>
      <Header />
      {children}
      <Footer />
    </div>
  );
}
